import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { extractErrorMessage } from "@platform/api";
import {
  type CaseListItemOut,
  type LatestRunOut,
  listCasesOptions,
} from "@platform/api/golden";
import { Alert, Link, Typography } from "@platform/design-system";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { tokenCost } from "../run/runCost";
import { ACCURACIES, totalOf } from "../run/runScore";
import { RfpLink } from "../shared/RfpLink";
import { EMPTY, percent } from "../shared/values";
import { TIER } from "../tier";

import { CaseStateChip } from "./CaseStateChip";

import type { ReactNode } from "react";

export const CASES_TABLE_TEST_ID = "golden-cases-table";

const LATEST_RUN_COLUMNS = [
  ...ACCURACIES.map(({ label }) => label),
  "RUMI-only fields",
  "Analyst-only fields",
  "Estimated cost",
];

/**
 * The latest run's accuracies, field counts, and cost, as its run page shows them
 * with no coverage chosen; dashes for a case with no scored run.
 */
const latestRunCells = (latest: LatestRunOut | null): readonly string[] => {
  if (!latest) return LATEST_RUN_COLUMNS.map(() => EMPTY);
  const buckets = latest.score.breakdown ?? [];
  const all = totalOf(buckets);
  const cost =
    latest.model && latest.tokens
      ? tokenCost(latest.model, latest.tokens)
      : undefined;
  return [
    ...ACCURACIES.map(({ category }) => {
      const { bothFilled, bothFilledCorrect } = totalOf(
        buckets.filter((bucket) => bucket.category === category),
      );
      return bothFilled === 0 ? EMPTY : percent(bothFilledCorrect / bothFilled);
    }),
    String(all.filled - all.bothFilled),
    String(all.blankExpected),
    cost === undefined ? EMPTY : `$${cost.toFixed(2)}`,
  ];
};

const CaseRow = ({
  goldenCase,
}: {
  readonly goldenCase: CaseListItemOut;
}): ReactNode => (
  <TableRow data-testid={`${CASES_TABLE_TEST_ID}-row-${goldenCase.id}`}>
    <TableCell>
      <Link component={RouterLink} to={`/cases/${goldenCase.id}`}>
        #{goldenCase.id}
      </Link>
    </TableCell>
    <TableCell>
      <CaseStateChip state={goldenCase.state} />
    </TableCell>
    <TableCell>{goldenCase.correction_revision}</TableCell>
    <TableCell sx={{ whiteSpace: "pre-wrap", maxWidth: 480 }}>
      {goldenCase.label || (
        <Typography
          variant="body-medium"
          color="textSecondary"
          component="span"
        >
          No label
        </Typography>
      )}
    </TableCell>
    <TableCell sx={{ fontFamily: "monospace" }}>
      <RfpLink id={goldenCase.source_rfp_id} url={goldenCase.source_rfp_url} />
    </TableCell>
    {latestRunCells(goldenCase.latest_run).map((text, index) => (
      <TableCell key={LATEST_RUN_COLUMNS[index]} align="right">
        {text}
      </TableCell>
    ))}
  </TableRow>
);

const casesCount = (count: number): string =>
  `${count} ${count === 1 ? "case" : "cases"}`;

/** "4 cases", or "1 of 4 cases" when the search leaves some out. */
const countText = (shown: number, total: number | undefined): string =>
  total === undefined || shown === total
    ? casesCount(shown)
    : `${shown} of ${casesCount(total)}`;

/** The golden cases whose label matches the search, or every case without one. */
export const CasesTable = ({
  search,
}: {
  readonly search: string;
}): ReactNode => {
  const client = useGoldenClient();
  const cases = useQuery(
    listCasesOptions({ client, query: search ? { search } : undefined }),
  );
  // The API lists every case at once, so the unsearched list's length is the
  // total. Without a search this is the same query as the one above.
  const allCases = useQuery(listCasesOptions({ client }));

  if (cases.isPending) {
    return (
      <CircularProgress
        data-testid={`${CASES_TABLE_TEST_ID}-loading`}
        aria-label="Loading cases"
      />
    );
  }
  if (cases.isError) {
    return (
      <Alert data-testid={`${CASES_TABLE_TEST_ID}-error`} severity="error">
        {extractErrorMessage(cases.error, "Failed to load cases")}
      </Alert>
    );
  }

  return (
    <TableContainer data-testid={CASES_TABLE_TEST_ID}>
      <Typography
        variant="body-medium"
        color="textSecondary"
        data-testid={`${CASES_TABLE_TEST_ID}-count`}
      >
        {countText(cases.data.length, allCases.data?.length)}
      </Typography>
      <Table size="small" aria-label={`${TIER} cases`}>
        <TableHead>
          <TableRow>
            <TableCell>Case</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Revision</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Source RFP</TableCell>
            {LATEST_RUN_COLUMNS.map((label) => (
              <TableCell key={label} align="right">
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {cases.data.map((goldenCase) => (
            <CaseRow key={goldenCase.id} goldenCase={goldenCase} />
          ))}
          {cases.data.length === 0 && (
            <TableRow data-testid={`${CASES_TABLE_TEST_ID}-empty`}>
              <TableCell colSpan={5 + LATEST_RUN_COLUMNS.length}>
                No cases.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
